import type { FilteredGameState, StateTransition, Domino, Play, Trick, TrumpSelection, Bid, GamePhase, LedSuit } from './types';
import { GAME_PHASES } from './index';
import { getSuitName, getTrumpDisplay } from './game-terms';
import type { DerivedViewFields } from '../multiplayer/types';

// Represents a player's bid status for UI display
export interface BidStatus {
  player: number;
  bid: Bid | null;
  isHighBidder: boolean;
  isDealer: boolean;
  isCurrentTurn: boolean;
  isThinking: boolean;
}

// Represents a domino in the player's hand with UI metadata
export interface HandDomino {
  domino: Domino;
  isPlayable: boolean;
  tooltip: string;
}

// Represents a play action that can be executed
export interface PlayAction {
  id: string;
  label: string;
  action: StateTransition;
}

// Represents the current trick display state
export interface TrickDisplay {
  plays: (Play | null)[];  // Indexed by player position (0-3)
  winner: number;  // -1 if not complete
  isComplete: boolean;
  currentPlayer: number;
}

// Represents hand scoring results
export interface HandResults {
  team0Points: number;
  team1Points: number;
  bidAmount: number;
  biddingTeam: number;
  bidMade: boolean;
  winningTeam: number;
  biddingPlayer: number;
  // Perspective-aware messages for each player
  resultMessage: string;  // Full message with emoji
  resultText: string;  // Text without emoji
  isSuccess: boolean;  // true for success, false for failure
  teamLabel: string;  // e.g., "US" or "THEM" for the bidding team
}

// Complete view projection for UI rendering
export interface ViewProjection {
  // Game phase and flow
  phase: GamePhase;
  currentPlayer: number;
  isPlayer0Turn: boolean;
  isPerspectiveTurn: boolean;
  perspectiveIndex: number;
  canAct: boolean;
  
  // Player's hand
  hand: HandDomino[];
  
  // Available actions grouped by type
  actions: {
    bidding: StateTransition[];
    trump: StateTransition[];
    proceed: StateTransition | null;  // Single proceed action if available
    consensus: StateTransition | null;  // Consensus action for player 0
    oneHandComplete: StateTransition[];  // retry/new for practice-hand terminal phase
  };
  
  // Bidding state
  bidding: {
    currentBid: { player: number; value?: number; type: string };
    winningBidder: number;
    dealer: number;
    playerStatuses: BidStatus[];  // One per player
    showBiddingTable: boolean;
  };
  
  // Trump state
  trump: {
    selection: TrumpSelection;
    display: string;  // Human-readable trump
  };
  
  // Trick state
  trick: {
    current: TrickDisplay;
    completed: Trick[];
    number: number;  // Current trick number (1-7)
    ledSuit: number;  // -1 if no suit led
    ledSuitDisplay: string | null;
    trickLeader: number | null;  // Player who led the current trick
  };
  
  // Scoring state
  scoring: {
    teamScores: [number, number];
    teamMarks: [number, number];
    currentHandPoints: [number, number];  // Current hand's trick points
    handResults: HandResults | null;  // Only during scoring phase
  };
  
  // UI state flags
  ui: {
    showActionPanel: boolean;
    showBiddingTable: boolean;
    isWaiting: boolean;
    waitingOnPlayer: number;  // -1 if not waiting
    isAIThinking: boolean;
    activeView: 'game' | 'actions';  // Which panel to show
  };
  
  // Tooltips and labels
  tooltips: {
    proceedAction: string | null;
    skipAction: string;
  };
}

// Create view projection from game state and available actions
// Uses server-computed derived fields for rule-based computations (dumb client pattern)
export function createViewProjection(
  gameState: FilteredGameState,
  availableActions: StateTransition[],
  options: {
    isTestMode?: boolean;
    viewingPlayerIndex?: number;
    canAct?: boolean;
    isAIControlled?: (player: number) => boolean;
    derived: DerivedViewFields;  // Server-computed derived fields (required - dumb client pattern)
  }
): ViewProjection {
  const {
    isTestMode = false,
    viewingPlayerIndex,
    canAct = true,
    isAIControlled = () => false,
    derived
  } = options;

  const phase = gameState.phase;
  const currentPlayer = gameState.currentPlayer;
  const perspectiveIndex = viewingPlayerIndex ?? 0;
  const isPerspectiveTurn = currentPlayer === perspectiveIndex;
  
  // Group actions by type
  const biddingActions = availableActions.filter(({ action }) =>
    action.type === 'bid' || action.type === 'pass' || action.type === 'redeal'
  );
  
  const trumpActions = availableActions.filter(({ action }) =>
    action.type === 'select-trump'
  );
  
  // Extract playable dominoes
  const playableDominoIds = new Set<string>();
  availableActions
    .filter(({ action }) => action.type === 'play')
    .forEach(({ action }) => {
      if (action.type !== 'play') return;
      const dominoId = action.dominoId;
      playableDominoIds.add(dominoId);
      const parts = dominoId.split('-');
      if (parts.length === 2) {
        playableDominoIds.add(`${parts[1]}-${parts[0]}`);
      }
    });
  
  // The selected perspective is authoritative for projected hand contents.
  // Turn ownership must never change whose hand a view displays (including
  // replay/test/review modes).
  const playerHand = gameState.players[perspectiveIndex]?.hand || [];

  // Build hand with metadata - use server-derived fields if available (dumb client pattern)
  const hand: HandDomino[] = playerHand.map(domino => {
    const dominoId = `${domino.high}-${domino.low}`;
    const isPlayable = playableDominoIds.has(dominoId) ||
                playableDominoIds.has(`${domino.low}-${domino.high}`);

    // Use server-computed tooltip hint (dumb client pattern - no fallbacks)
    const serverMeta = derived.handDominoMeta.find(
      m => m.dominoId === dominoId || m.dominoId === `${domino.low}-${domino.high}`
    );
    const tooltip = serverMeta?.tooltipHint
      ? `${dominoId} - ${serverMeta.tooltipHint}`
      : dominoId;

    return { domino, isPlayable, tooltip };
  });
  
  // Find proceed and consensus actions
  const proceedAction = availableActions.find(({ action }) =>
    action.type === 'complete-trick' || action.type === 'score-hand'
  ) || null;

  const oneHandCompleteActions = phase === 'one-hand-complete'
    ? availableActions.filter(({ action }) =>
        action.type === 'retry-one-hand' || action.type === 'new-one-hand'
      )
    : [];

  const consensusAction = availableActions.find(({ action }) =>
    (action.type === 'agree-trick' || action.type === 'agree-score') &&
    action.player === perspectiveIndex
  ) || null;
  const actionableActions = canAct ? availableActions : [];
  const isConsensusPhase = actionableActions.length > 0 &&
    actionableActions.every(({ action }) =>
      action.type === 'agree-trick' || action.type === 'agree-score'
    );
  
  // Build bidding statuses
  const biddingStatuses: BidStatus[] = [0, 1, 2, 3].map(playerId => ({
    player: playerId,
    bid: gameState.bids.find(b => b.player === playerId) || null,
    isHighBidder: gameState.currentBid.player === playerId,
    isDealer: gameState.dealer === playerId,
    isCurrentTurn: currentPlayer === playerId && !isConsensusPhase,
    isThinking: currentPlayer === playerId && isAIControlled(playerId) && !isConsensusPhase
  }));
  
  // Trump display
  const trumpDisplay = getTrumpDisplay(gameState.trump);

  // Current trick display - use server-computed trick winner (dumb client pattern)
  const currentTrickDisplay: TrickDisplay = {
    plays: [0, 1, 2, 3].map(p =>
      gameState.currentTrick.find(play => play.player === p) || null
    ),
    winner: derived.currentTrickWinner,
    isComplete: derived.isTrickComplete,
    currentPlayer
  };
  
  // Trick number
  const trickNumber = phase === 'scoring' || phase === 'bidding'
    ? gameState.tricks.length
    : Math.min(gameState.tricks.length + 1, 7);
  
  // Led suit display
  const ledSuitDisplay = gameState.currentSuit >= 0 && gameState.currentSuit <= 7
    ? getSuitName(gameState.currentSuit as LedSuit)
    : null;
  
  // Hand results (only during scoring), always phrased for the selected
  // perspective rather than the player whose turn happens to be current.
  const playerPerspective = perspectiveIndex;
  const handResults =
    phase === 'scoring' || phase === 'game_end' || phase === 'one-hand-complete'
    ? calculateHandResults(gameState, playerPerspective, derived)
    : null;
  
  // Determine waiting state
  const playerActions = actionableActions.filter(({ action }) =>
    action.type === 'bid' ||
    action.type === 'pass' ||
    action.type === 'redeal' ||
    action.type === 'select-trump' ||
    action.type === 'play'
  );
  
  const hasActions = playerActions.length > 0;
  
  const isWaitingDuringBidding = phase === 'bidding' && canAct && !isConsensusPhase && (!isPerspectiveTurn || !hasActions);
  const isWaitingDuringTrump = phase === 'trump_selection' && canAct && !isConsensusPhase && (!isPerspectiveTurn || !hasActions);
  const isWaitingDuringPlay = phase === 'playing' && canAct && !isConsensusPhase && (!isPerspectiveTurn || !hasActions);
  const isWaiting = isWaitingDuringBidding || isWaitingDuringTrump || isWaitingDuringPlay;
  const waitingOnPlayer = isWaiting ? currentPlayer : -1;
  const isAIThinking = waitingOnPlayer >= 0 && isAIControlled(waitingOnPlayer);
  
  // Determine active view
  let activeView: 'game' | 'actions' = 'game';
  if (phase === GAME_PHASES.BIDDING || phase === GAME_PHASES.TRUMP_SELECTION) {
    activeView = 'actions';
  }
  
  return {
    phase,
    currentPlayer,
    isPlayer0Turn: isPerspectiveTurn,
    isPerspectiveTurn,
    perspectiveIndex,
    canAct,
    hand,
    actions: {
      bidding: canAct ? biddingActions : [],
      trump: canAct ? trumpActions : [],
      proceed: canAct ? (proceedAction || consensusAction) : null,
      consensus: canAct ? consensusAction : null,
      oneHandComplete: canAct ? oneHandCompleteActions : []
    },
    bidding: {
      currentBid: gameState.currentBid,
      winningBidder: gameState.winningBidder,
      dealer: gameState.dealer,
      playerStatuses: biddingStatuses,
      showBiddingTable: isWaitingDuringBidding && !isTestMode
    },
    trump: {
      selection: gameState.trump,
      display: trumpDisplay
    },
    trick: {
      current: currentTrickDisplay,
      completed: gameState.tricks,
      number: trickNumber,
      ledSuit: gameState.currentSuit,
      ledSuitDisplay,
      trickLeader: gameState.currentTrick[0]?.player ?? null
    },
    scoring: {
      teamScores: gameState.teamScores,
      teamMarks: gameState.teamMarks,
      currentHandPoints: derived.currentHandPoints,
      handResults
    },
    ui: {
      showActionPanel: canAct && (phase === 'bidding' || phase === 'trump_selection'),
      showBiddingTable: isWaitingDuringBidding && !isTestMode,
      isWaiting,
      waitingOnPlayer,
      isAIThinking,
      activeView
    },
    tooltips: {
      proceedAction: canAct ? (proceedAction?.label || consensusAction?.label || null) : null,
      skipAction: isAIThinking ? `P${waitingOnPlayer} is thinking...` : ''
    }
  };
}

// Note: getDominoTooltip has been moved to kernel/kernel.ts (computeTooltipHint)
// to implement the dumb client pattern - all rule-based computations happen server-side.

// Note: getTrumpDisplay is now imported from game-terms.ts

// Helper to calculate hand results with perspective-aware messages
function calculateHandResults(
  gameState: FilteredGameState,
  playerPerspective: number,
  derived: DerivedViewFields,
): HandResults | null {
  const outcome = derived.handOutcome;
  if (!outcome) return null;

  const [team0Points, team1Points] = derived.currentHandPoints;

  // Bid amount is presentational metadata; contract success and winner come
  // from the authoritative rules-derived outcome above.
  const requiredPoints = (gameState.currentBid.type === 'points' && gameState.currentBid.value != null)
    ? gameState.currentBid.value
    : 42;
  
  const biddingPlayer = gameState.winningBidder;
  const biddingTeam = outcome.biddingTeam;
  const bidMade = outcome.contractMade;
  
  // Determine player's team from player object
  const playerTeam = gameState.players[playerPerspective]?.teamId;
  
  if (playerTeam === undefined) {
    return null; // Invalid player perspective
  }
  
  // Generate perspective-aware messages
  let resultMessage: string;
  let resultText: string;
  let isSuccess: boolean;
  let teamLabel: string;
  
  // Determine if the bidding team is "us" or "them" from player's perspective
  const isBiddingTeamUs = biddingTeam === playerTeam;
  teamLabel = isBiddingTeamUs ? 'US' : 'THEM';
  
  // Generate result message based on perspective
  if (isBiddingTeamUs) {
    // We bid
    if (bidMade) {
      resultMessage = '✅ WE MADE THE BID!';
      resultText = 'WE MADE THE BID!';
      isSuccess = true;
    } else {
      resultMessage = '❌ WE GOT SET!';
      resultText = 'WE GOT SET!';
      isSuccess = false;
    }
  } else {
    // They bid
    if (bidMade) {
      resultMessage = '❌ THEY MADE THE BID!';
      resultText = 'THEY MADE THE BID!';
      isSuccess = false;
    } else {
      resultMessage = '✅ WE SET THEM!';
      resultText = 'WE SET THEM!';
      isSuccess = true;
    }
  }
  
  return {
    team0Points,
    team1Points,
    bidAmount: requiredPoints,
    biddingTeam,
    bidMade,
    winningTeam: outcome.winningTeam,
    biddingPlayer,
    resultMessage,
    resultText,
    isSuccess,
    teamLabel
  };
}
